import { NavLink, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard,
  Search,
  Kanban,
  Building2,
  Hammer,
  Landmark,
  Home,
  Wrench,
  ClipboardCheck,
  Calculator,
  Receipt,
  Settings,
  ChevronLeft,
  ChevronRight,
  Shield,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import { WorkspaceSwitcher } from "@/components/workspace/WorkspaceSwitcher";
import { useAuth } from "@/hooks/use-auth";
import { useFeatureFlags } from "@/hooks/use-feature-flags";
import { useWorkspace } from "@/hooks/use-workspace";
import {
  APP_ROUTE_FEATURE_FLAG,
  type FeatureFlagKey,
} from "@/lib/feature-flags";
import type { Permission } from "@/lib/permissions";
import syntraSymbol from "@/assets/syntra-symbol.svg";
import type { LucideIcon } from "lucide-react";

interface NavItemConfig {
  to: string;
  label: string;
  icon: LucideIcon;
  /** When set, the item is hidden unless the flag is enabled for the workspace. */
  flagKey?: FeatureFlagKey;
  /** When set, the item is hidden unless the current member has this permission. */
  permission?: Permission;
}

const navItems: NavItemConfig[] = [
  {
    to: "/dashboard",
    label: "Dashboard",
    icon: LayoutDashboard,
    flagKey: APP_ROUTE_FEATURE_FLAG["/dashboard"],
  },
  {
    to: "/deals",
    label: "Find",
    icon: Search,
    flagKey: APP_ROUTE_FEATURE_FLAG["/deals"],
    // permission: "deals.view",  // disabled — deals permission removed

  },
  {
    to: "/pipeline",
    label: "Pipeline",
    icon: Kanban,
    flagKey: APP_ROUTE_FEATURE_FLAG["/pipeline"],
    permission: "pipeline.view",
  },
  {
    to: "/properties",
    label: "Properties",
    icon: Building2,
    flagKey: APP_ROUTE_FEATURE_FLAG["/properties"],
    permission: "properties.view",
  },
  {
    to: "/rehab",
    label: "Rehab",
    icon: Hammer,
    flagKey: APP_ROUTE_FEATURE_FLAG["/rehab"],
    permission: "rehab.view",
  },
  {
    to: "/financing",
    label: "Financing",
    icon: Landmark,
    flagKey: APP_ROUTE_FEATURE_FLAG["/financing"],
    permission: "loans.view",
  },
  {
    to: "/rentals",
    label: "Rentals",
    icon: Home,
    flagKey: APP_ROUTE_FEATURE_FLAG["/rentals"],
    permission: "rentals.view",
  },
  {
    to: "/property-mgmt",
    label: "Property Mgmt",
    icon: Wrench,
    flagKey: APP_ROUTE_FEATURE_FLAG["/property-mgmt"],
    permission: "property_mgmt.view",
  },
  {
    to: "/section-8",
    label: "Section 8",
    icon: ClipboardCheck,
    flagKey: APP_ROUTE_FEATURE_FLAG["/section-8"],
    permission: "section8.view",
  },
  {
    to: "/accounting",
    label: "Accounting",
    icon: Calculator,
    flagKey: APP_ROUTE_FEATURE_FLAG["/accounting"],
    permission: "accounting.view",
  },
  {
    to: "/taxes",
    label: "Taxes",
    icon: Receipt,
    flagKey: APP_ROUTE_FEATURE_FLAG["/taxes"],
    permission: "accounting.view",
  },
];

const bottomNavItems: NavItemConfig[] = [
  {
    to: "/settings",
    label: "Settings",
    icon: Settings,
    flagKey: APP_ROUTE_FEATURE_FLAG["/settings"],
  },
];

export function AppSidebar() {
  const [collapsed, setCollapsed] = useState(false);
  const location = useLocation();
  const { canAccessAdmin, adminRoleReady } = useAuth();
  const { isEnabled, isLoading: flagsLoading } = useFeatureFlags();
  const { canDo, isLoading: workspaceLoading } = useWorkspace();

  const visibleNavItems = navItems.filter((item) => {
    if (item.flagKey) {
      if (flagsLoading) return false;
      if (!isEnabled(item.flagKey)) return false;
    }
    if (item.permission) {
      if (workspaceLoading) return false;
      if (!canDo(item.permission)) return false;
    }
    return true;
  });

  return (
    <aside
      className={cn(
        "flex flex-col h-screen bg-sidebar border-r border-sidebar-border transition-all duration-300",
        collapsed ? "w-16" : "w-64",
      )}
    >
      {/* Logo */}
      <div className="flex items-center h-16 px-4 border-b border-sidebar-border">
        <div className="flex items-center gap-3">
          <img src={syntraSymbol} alt="Syntra" className="w-8 h-8" />
          {!collapsed && (
            <span className="font-bold text-lg gradient-syntra-blue-text">
              SYNTRA
            </span>
          )}
        </div>
      </div>

      {/* Workspace Switcher */}
      <div className="p-2 border-b border-sidebar-border">
        <WorkspaceSwitcher collapsed={collapsed} />
      </div>

      {/* Main Navigation */}
      <nav className="flex-1 py-4 overflow-y-auto">
        <ul className="space-y-1 px-2">
          {visibleNavItems.map((item) => {
            const isActive = location.pathname === item.to;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Bottom Navigation */}
      <div className="border-t border-sidebar-border py-4">
        <ul className="space-y-1 px-2">
          {adminRoleReady && canAccessAdmin && (
            <li>
              <NavLink
                to="/admin"
                className={cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  location.pathname.startsWith("/admin")
                    ? "bg-secondary/10 text-secondary"
                    : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                )}
              >
                <Shield className="w-5 h-5 shrink-0" />
                {!collapsed && <span>Admin</span>}
              </NavLink>
            </li>
          )}
          {bottomNavItems.map((item) => {
            if (item.flagKey && (flagsLoading || !isEnabled(item.flagKey))) {
              return null;
            }
            if (
              item.permission &&
              (workspaceLoading || !canDo(item.permission))
            ) {
              return null;
            }
            const isActive = location.pathname === item.to;
            return (
              <li key={item.to}>
                <NavLink
                  to={item.to}
                  className={cn(
                    "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                    isActive
                      ? "bg-primary/10 text-primary"
                      : "text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground",
                  )}
                >
                  <item.icon className="w-5 h-5 shrink-0" />
                  {!collapsed && <span>{item.label}</span>}
                </NavLink>
              </li>
            );
          })}
        </ul>

        {/* Collapse Toggle */}
        <div className="px-2 mt-4">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setCollapsed(!collapsed)}
            className="w-full justify-center text-sidebar-foreground hover:bg-sidebar-accent"
          >
            {collapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <>
                <ChevronLeft className="w-4 h-4 mr-2" />
                <span>Collapse</span>
              </>
            )}
          </Button>
        </div>
      </div>
    </aside>
  );
}
